import { useCallback, useMemo, useState } from "react";
import { catchError, of, tap, type Observable } from "rxjs";

import {
  mockReviewQueue,
  type ReviewQueueItem,
  type ReviewQueueStatus,
} from "@/app/library/data_sources/mock";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  adminSelectClasses,
  AdminStatCard,
  AdminTableToolbar,
  Banner,
  DataTable,
  PageHeader,
} from "@/core/ui/molecules";
import { DialogTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { ChipList, StatusBadge, TypeBadge } from "../../components/molecules";
import {
  ImportTemplateDialog,
  type ImportedTemplateDraft,
} from "./components/ImportTemplateDialog";
import { ReviewTemplateDrawer } from "./components/ReviewTemplateDrawer";
import { LibraryTemplatesStore } from "../templates/data_sources/store";

type SourceFilter = "all" | ReviewQueueItem["source"];
type StatusFilter = "all" | ReviewQueueStatus;
type TypeFilter = "all" | ReviewQueueItem["type"];

export function ReviewQueuePage() {
  const { actions: appActions } = useStore(AppStore.instance);
  const {
    actions: templateActions,
    state: templatesState,
    hooks: templateHooks,
  } = useStore(LibraryTemplatesStore.instance);
  const [actionError, setActionError] = useState("");
  const [items, setItems] = useState<ReviewQueueItem[]>([...mockReviewQueue]);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  templateHooks.useTemplateStoreService({
    page: 1,
    page_size: 100,
  });
  const closeDialog = useCallback(() => {
    appActions.dialog("close");
  }, [appActions]);
  const closeDrawer = useCallback(() => {
    appActions.rightDrawer("close");
  }, [appActions]);
  const mutateStatus = useCallback(
    (id: string, status: ReviewQueueStatus, changes?: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, changes, status } : item,
        ),
      );
      closeDrawer();
    },
    [closeDrawer],
  );
  const runTemplateAction = useCallback(
    (
      id: string,
      action: (templateId: number) => Observable<unknown>,
      onSuccess: () => void,
      fallback: () => void,
    ) => {
      const templateId = Number(id);
      if (!Number.isFinite(templateId)) {
        fallback();
        return;
      }

      setActionError("");
      action(templateId)
        .pipe(
          tap(() => {
            onSuccess();
          }),
          catchError((requestError) => {
            setActionError(
              requestError instanceof ErrorResponse
                ? requestError.error.message
                : requestError instanceof Error
                  ? requestError.message
                  : "Unable to update template.",
            );
            return of(null);
          }),
        )
        .subscribe();
    },
    [],
  );
  const publishItem = useCallback(
    (id: string) => {
      runTemplateAction(
        id,
        templateActions.publishTemplate,
        () => {
          setItems((prev) => prev.filter((item) => item.id !== id));
          closeDrawer();
        },
        () => {
          setItems((prev) => prev.filter((item) => item.id !== id));
          closeDrawer();
        },
      );
    },
    [closeDrawer, runTemplateAction, templateActions.publishTemplate],
  );
  const archiveItem = useCallback(
    (id: string) => {
      runTemplateAction(
        id,
        templateActions.archiveTemplate,
        () => mutateStatus(id, "Archived", "Archived by Super Admin"),
        () => mutateStatus(id, "Archived", "Archived by Super Admin"),
      );
    },
    [mutateStatus, runTemplateAction, templateActions.archiveTemplate],
  );
  const handleImportedTemplate = useCallback((draft: ImportedTemplateDraft) => {
    setItems((prev) => [
      {
        id: String(draft.templateId ?? `rq-${Date.now()}`),
        source: "Imported Drafts",
        name: draft.name,
        type: draft.type,
        verticals: draft.verticals,
        category: draft.category,
        status: "Pending Review",
        version: draft.version,
        fields: draft.fields,
        submittedBy: "Super Admin",
        submittedFrom: "JSON Import",
        description: draft.description,
      },
      ...prev,
    ]);
  }, []);
  const openImportDialog = useCallback(() => {
    appActions.dialog(
      "open",
      <DialogTemplate className="max-w-none">
        <ImportTemplateDialog
          onClose={closeDialog}
          onImport={handleImportedTemplate}
        />
      </DialogTemplate>,
    );
  }, [appActions, closeDialog, handleImportedTemplate]);
  const openReviewDrawer = useCallback(
    (item: ReviewQueueItem) => {
      appActions.rightDrawer(
        "open",
        620,
        <ReviewTemplateDrawer
          item={item}
          onArchive={archiveItem}
          onClose={closeDrawer}
          onPublish={publishItem}
          onReject={(id) =>
            mutateStatus(id, "Rejected", "Rejected by Super Admin")
          }
          onRequestChanges={(id) =>
            mutateStatus(id, "Changes Requested", "Changes requested")
          }
        />,
      );
    },
    [appActions, archiveItem, closeDrawer, mutateStatus, publishItem],
  );
  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return items.filter((item) => {
      const haystack = Object.values(item).join(" ").toLowerCase();

      return (
        (!normalizedSearch || haystack.includes(normalizedSearch)) &&
        (sourceFilter === "all" || item.source === sourceFilter) &&
        (statusFilter === "all" || item.status === statusFilter) &&
        (typeFilter === "all" || item.type === typeFilter)
      );
    });
  }, [items, search, sourceFilter, statusFilter, typeFilter]);
  const publishedTemplatesCount = useMemo(
    () =>
      templatesState?.list.filter(
        (template) => template.getData().status === "published",
      ).length ?? 0,
    [templatesState?.list],
  );
  const columns = useMemo(
    () => [
      {
        header: "Template",
        key: "template",
        render: (item: ReviewQueueItem) => (
          <button
            className="max-w-xl text-left"
            onClick={() => openReviewDrawer(item)}
            type="button"
          >
            <div className="font-extrabold text-ink">{item.name}</div>
            <div className="text-xs text-ink-muted">
              {item.description} · {item.version}
            </div>
          </button>
        ),
      },
      {
        header: "Source",
        key: "source",
        render: (item: ReviewQueueItem) => <StatusBadge status={item.source} />,
      },
      {
        header: "Type",
        key: "type",
        render: (item: ReviewQueueItem) => <TypeBadge type={item.type} />,
      },
      {
        header: "Verticals",
        key: "verticals",
        render: (item: ReviewQueueItem) => <ChipList values={item.verticals} />,
      },
      {
        header: "Category",
        key: "category",
        render: (item: ReviewQueueItem) => item.category,
      },
      {
        header: "Status",
        key: "status",
        render: (item: ReviewQueueItem) => <StatusBadge status={item.status} />,
      },
      {
        header: "Submitted by",
        key: "submittedBy",
        render: (item: ReviewQueueItem) => (
          <span className="text-xs text-ink-muted">
            {item.submittedBy}
            <br />
            {item.submittedFrom}
          </span>
        ),
      },
      {
        className: "w-64",
        header: "",
        key: "actions",
        render: (item: ReviewQueueItem) => (
          <div className="flex flex-wrap justify-end gap-2">
            <Button onClick={() => openReviewDrawer(item)} size="sm">
              Open
            </Button>
            {item.status === "Pending Review" ? (
              <>
                <Button onClick={() => publishItem(item.id)} size="sm">
                  Publish
                </Button>
                <Button
                  onClick={() =>
                    mutateStatus(
                      item.id,
                      "Changes Requested",
                      "Changes requested",
                    )
                  }
                  size="sm"
                >
                  Request changes
                </Button>
              </>
            ) : null}
            {item.status !== "Archived" ? (
              <Button
                onClick={() =>
                  mutateStatus(item.id, "Rejected", "Rejected by Super Admin")
                }
                size="sm"
                variant="danger"
              >
                Reject
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [mutateStatus, openReviewDrawer, publishItem],
  );
  const pending = items.filter(
    (item) => item.status === "Pending Review",
  ).length;

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={openImportDialog}>Create from Existing Model</Button>
        }
        breadcrumb={["Library", "Review Queue"]}
        subtitle="Central approval queue for templates before they enter the global Library catalog."
        title="Review Queue"
      />
      <Banner tone="ok">
        <strong>Rule:</strong> Super Admin reviews, approves and publishes. It
        does not design or edit template structure. Bad templates are rejected
        or sent back with requested changes.
      </Banner>
      {actionError ? (
        <Banner tone="error">
          <strong>Template action failed:</strong> {actionError}
        </Banner>
      ) : null}
      <div className="mb-[18px] grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Pending Review" value={pending} />
        <AdminStatCard
          label="Imported Drafts"
          value={
            items.filter((item) => item.source === "Imported Drafts").length
          }
        />
        <AdminStatCard
          label="Tenant Submissions"
          value={
            items.filter((item) => item.source === "Tenant Submissions").length
          }
        />
        <AdminStatCard
          label="Published Catalog"
          value={publishedTemplatesCount}
        />
      </div>
      <DataTable
        columns={columns}
        header={
          <AdminTableToolbar>
            <input
              className={`${adminInputClasses} w-80 max-w-full`}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search review queue..."
              value={search}
            />
            <div className="flex flex-wrap items-center gap-2">
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setSourceFilter(event.target.value as SourceFilter)
                }
                value={sourceFilter}
              >
                <option value="all">All sources</option>
                <option>Imported Drafts</option>
                <option>Tenant Submissions</option>
              </select>
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setStatusFilter(event.target.value as StatusFilter)
                }
                value={statusFilter}
              >
                <option value="all">All status</option>
                <option>Pending Review</option>
                <option>Changes Requested</option>
                <option>Rejected</option>
                <option>Archived</option>
              </select>
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setTypeFilter(event.target.value as TypeFilter)
                }
                value={typeFilter}
              >
                <option value="all">All types</option>
                <option>Form</option>
                <option>Media</option>
                <option>Model</option>
              </select>
            </div>
          </AdminTableToolbar>
        }
        rowKey={(item) => item.id}
        rows={filteredItems}
      />
      <Banner>
        <strong>Allowed actions:</strong> Open detail, preview, review metadata,
        verticals and categories, publish, reject, archive or request changes.{" "}
        <strong>Blocked:</strong> Form Builder, Media Builder, Model Builder and
        structure edits.
      </Banner>
    </>
  );
}
